import isString from 'lodash-es/isString';

import { camelizeString, snakeifyString } from '@rover/rsdk/src/modules/Network/object-utilities';
import {
  EMPTY_ALLOWABLE_FIELDS,
  PROVIDE_AS_ARRAY_IN_QUERY_FIELDS,
} from '@rover/shared/js/constants/searchPage.constants';
import { isFalsyOrEmpty } from '@rover/shared/js/search/utilities/filtersToQuery';
import type { SearchFilters } from '@rover/types';

// These keys map to the request params for the search API, since some of them
// are nonstandard, eg: noChildren05: 'no_children_0_5'. All of the snake cased
// keys are supported in the search API, so if someone puts one of them into the
// query, the search API will use it to return the correct results.
const searchFilterKeysMap = {
  acceptsOnlyOneClient: 'accepts_only_one_client',
  adwordslocation: 'adwordslocation',
  alternateResults: 'alternate_results',
  agency: 'agency',
  apse: 'apse',
  bathingGrooming: 'bathing_grooming',
  bestMatchOffset: 'best_match_offset',
  catCare: 'cat_care',
  catCount: 'cat_count',
  centerlat: 'centerlat',
  centerlng: 'centerlng',
  days: 'days',
  dogPreference: 'dog_preference',
  dogsAllowedOnBed: 'dogs_allowed_on_bed',
  dogsAllowedOnFurniture: 'dogs_allowed_on_furniture',
  dogSize: 'dog_size',
  dogCount: 'dog_count',
  endDate: 'end_date',
  eveningAvailability: 'evening_availability',
  excludedPersonOpks: 'excluded_person_opks',
  frequency: 'frequency',
  friday: 'friday',
  fulltimeAvailability: 'fulltime_availability',
  fullprice: 'fullprice',
  giantDogs: 'giant_dogs',
  hasFencedYard: 'has_fenced_yard',
  hasHouse: 'has_house',
  hasNoChildren: 'has_no_children',
  isInitialSearch: 'is_initial_search',
  isPremier: 'is_premier',
  knowsFirstAid: 'knows_first_aid',
  largeDogs: 'large_dogs',
  location: 'location',
  locationAccuracy: 'location_accuracy',
  locationType: 'location_type',
  maxlat: 'maxlat',
  maxlng: 'maxlng',
  maxprice: 'maxprice',
  mediumDogs: 'medium_dogs',
  middayAvailability: 'midday_availability',
  minlat: 'minlat',
  minlng: 'minlng',
  minprice: 'minprice',
  minRating: 'min_rating',
  monday: 'monday',
  morningAvailability: 'morning_availability',
  noCagedPets: 'no_caged_pets',
  noCats: 'no_cats',
  noChildren05: 'no_children_0_5',
  noChildren612: 'no_children_6_12',
  nonSmoking: 'non_smoking',
  page: 'page',
  personDoesNotHaveDogs: 'person_does_not_have_dogs',
  pet: 'pet',
  petsitusa: 'petsitusa',
  petType: 'pet_type',
  puppy: 'puppy',
  puppyCount: 'puppy_count',
  providerGroup: 'provider_group',
  rawLocationTypes: 'raw_location_types',
  residencePreference: 'residence_preference',
  saturday: 'saturday',
  seoLocationSlug: 'seo_location_slug',
  serviceType: 'service_type',
  smallDogs: 'small_dogs',
  spacesRequired: 'spaces_required',
  starSitter: 'star_sitter',
  startDate: 'start_date',
  sunday: 'sunday',
  thursday: 'thursday',
  tuesday: 'tuesday',
  wednesday: 'wednesday',

  searchScoreDebug: 'search_score_debug',
  zoomlevel: 'zoomlevel',

  injectedMedication: 'injected_medication',
  searchCurrentProvider: 'search_current_provider',
  inUnlaunchedCountry: 'in_unlaunched_country',
  specialNeeds: 'special_needs',
  oralMedication: 'oral_medication',
  moreThanOneClient: 'more_than_one_client',
  uncratedDogs: 'uncrated_dogs',
  unspayedFemales: 'unspayed_females',
  nonNeuteredMales: 'non_neutered_males',
  femalesInHeat: 'females_in_heat',
  premierMatching: 'premier_matching',
  premierOrRoverMatch: 'premier_or_rover_match',
  isMemberOfSitterToSitter: 'is_member_of_sitter_to_sitter',
  isMemberOfSitterToSitterPlus: 'is_member_of_sitter_to_sitter_plus',
  isAcceptingNewRecurringClients: 'is_accepting_new_recurring_clients',
  timeAlone: 'time_alone',
  selectedProvider: 'selected_provider',
  selectedFacility: 'selected_facility',
  bypassInterstitial: 'bypass_interstitial',
  daycareType: 'daycare_type',
  businessType: 'business_type',
};

// This is a reverse mapping of the `searchFilterKeysMap` above
const searchApiKeysMap = {};
Object.keys(searchFilterKeysMap).forEach((filterName) => {
  const apiName = searchFilterKeysMap[filterName];
  searchApiKeysMap[apiName] = filterName;
});

// This function takes camel cased keys and converts them to snake cased keys,
// for the search API and the URL query. It uses the `searchfilterKeysMap`
// above.
const convertToSearchApiKeys = ({
  filters,
  allowAllKeys = false,
}: {
  filters: Partial<SearchFilters>;
  allowAllKeys?: boolean;
}): Record<string, any> => {
  const snaked = {};

  Object.keys(filters).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(searchFilterKeysMap, key)) {
      snaked[searchFilterKeysMap[key]] = filters[key];
    } else if (allowAllKeys) {
      snaked[snakeifyString(key)] = filters[key];
    }
  });

  return snaked;
};

// This function takes snake cased keys and converts them to camel cased keys,
// for controlling UI.
const convertToSearchFilterKeys = ({
  query,
  allowAllKeys = false,
}: {
  query?: any; // matches the query type in redux-duck/types.js
  allowAllKeys?: boolean;
}): Partial<SearchFilters> => {
  const cameled = {};
  if (!query) return cameled;

  Object.keys(query).forEach((key) => {
    if (!isFalsyOrEmpty(query[key]) || EMPTY_ALLOWABLE_FIELDS.has(searchApiKeysMap[key])) {
      if (searchApiKeysMap[key]) {
        // ensure certain comma separated strings are provided as a JS array
        if (PROVIDE_AS_ARRAY_IN_QUERY_FIELDS.has(searchApiKeysMap[key]) && isString(query[key])) {
          cameled[searchApiKeysMap[key]] = query[key].split(',');
        } else {
          cameled[searchApiKeysMap[key]] = query[key];
        }
      } else if (allowAllKeys) {
        cameled[camelizeString(key)] = query[key];
      }
    }
  });

  return cameled;
};

export { searchFilterKeysMap, searchApiKeysMap, convertToSearchApiKeys, convertToSearchFilterKeys };
